import React from 'react';
import './CalculusOfMeaning.css';

function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

function GnulChapter4() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <div className="calculus-hero-overlay" style={{ background: 'linear-gradient(to bottom, rgba(8,8,8,0.5) 0%, rgba(8,8,8,0.8) 60%, rgba(8,8,8,1) 100%)' }} />
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundImage: 'url(/assets/gnul-hero.png)', backgroundSize: 'cover', backgroundPosition: 'center 20%', zIndex: 1 }} />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">The Chemistry of Leadership &middot; Chapter 4</span>
          <h1>The Meridian</h1>
          <p className="calculus-hero-sub">The body cannot process what the mind has not yet accepted. He needed the night. The night needed him still.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <div className="calculus-content">

          {/* Arrival */}
          <section className="calc-section reveal">
            <h2>The Lobby</h2>
            <p className="calc-lead">
              The car pulls up to a building made of dark glass and warm light. The
              Meridian. The sign is small. Understated. The kind of place that does not
              need to announce itself because the people who belong here already know
              where it is.
            </p>
            <p>
              He does not belong here. He knows that. His body knows it before his mind
              confirms it. The doorman sees the car and steps forward. Then sees him step
              out and the doorman's rhythm breaks. One half-step of hesitation. A blink
              that lasts a fraction too long. The recalibration. The recovery. The
              professional mask sliding back into place.
            </p>
            <p>
              "Welcome to The Meridian, sir."
            </p>
            <p>
              The voice is steady. Trained. But not neutral the way the butler was neutral.
              This man's neutral has a seam in it. The cortisol spike happened. He simply
              covered it faster than most.
            </p>
            <p>
              Gnul walks into the lobby. Marble floors. Low lighting. A fireplace that
              is not burning wood but produces warmth anyway. The air smells like cedar
              and something floral he cannot name. Everything in this room has been
              designed to produce one neurochemical response: serotonin. Stability.
              Calm. The architecture of a space that says nothing will surprise you here.
            </p>
            <p>
              Everything surprises him here. He has never been in a building that was
              not the cabin.
            </p>
          </section>

          <Pulse />

          {/* The Front Desk */}
          <section className="calc-section reveal">
            <h2>The Front Desk</h2>
            <p>
              The woman behind the counter looks up. Her face does something he has
              seen a hundred times today and will see a thousand times tomorrow. The
              flash. The widening. The micro-contraction around the mouth. And then,
              because she is a professional in a building that serves people who expect
              perfection, the recovery. Faster than the doorman. Almost seamless.
            </p>
            <p>
              Almost.
            </p>
            <p>
              "Good evening. Do you have a reservation?"
            </p>
            <p>
              "I think so. The name is Gnul."
            </p>
            <p>
              She types. Her eyes move to the screen and stay there slightly too long.
              She is reading something. Not just his name. Something attached to his
              name. Her fingers stop moving. Her posture changes. It is subtle. A
              straightening of the spine. A shift in the angle of her chin. Whatever
              is on that screen has changed the way she holds her body.
            </p>
            <p>
              "Mr. Gnul. Yes. We have you in the Penthouse Suite. Ms. Loriann arranged
              everything. You will find the room fully stocked. If there is anything
              you need, anything at all, my direct line is on the card inside the room."
            </p>
            <p>
              Her voice has changed too. Not the content. The frequency. Lower. Steadier.
              The deference that appears when a service professional encounters a guest
              whose reservation carries a flag they are trained to recognize. Whatever
              Loriann put in the booking, it told this woman something about who is
              standing in front of her that tusks alone did not communicate.
            </p>
            <p>
              "Thank you."
            </p>
            <p>
              "The elevator is to your left. Top floor. It will open directly into your
              suite." A pause. "Welcome, sir. Truly."
            </p>
            <p>
              The "truly" is real. He can hear it. Whatever was on that screen dissolved
              the fear. She is not performing calm. She is calm. Something about who he
              is to whoever booked this room has rewritten the front desk woman's
              neurochemical assessment faster than his face could set it.
            </p>
            <p>
              Loriann. Again. Building trust ahead of him like someone laying stones
              across a river so he can cross without getting wet.
            </p>
          </section>

          <Pulse />

          {/* The Room */}
          <section className="calc-section reveal">
            <h2>The Room</h2>
            <p>
              The elevator opens into silence.
            </p>
            <p>
              The suite is larger than the cabin. That is the first thing he processes
              and it almost breaks him. He has lived his entire life in four rooms and
              a kitchen and this hotel room has more space than his entire existence up
              to this morning.
            </p>
            <p>
              Floor-to-ceiling windows. The city below. Lights moving in patterns that
              look like the neural diagrams in his caretaker's textbooks. Branching.
              Connecting. Firing. A city at night is a nervous system seen from above
              and he stands at the window and watches it breathe.
            </p>
            <p>
              There is food on the table. Not room service. Not a menu card. Actual
              food. A plate covered with a cloth. He lifts it. Grilled meat. Vegetables
              he recognizes from the garden his caretaker kept. Roasted potatoes with
              rosemary. A glass of water with ice and lemon.
            </p>
            <p>
              It looks like home.
            </p>
            <p>
              He sits down at the table and his eyes burn and his throat closes and the
              oxytocin hits so hard it buckles something inside him that has been holding
              all day. Since the letter. Since the door. Since the terminal and the
              scream and the four hundred pairs of eyes and the phone and the number and
              the attorney and the Chief of Staff and Loriann and the plane and the city
              and this room that smells like cedar and has food that looks like his
              caretaker made it.
            </p>
            <p>
              He cries.
            </p>
            <p>
              Not the way the woman at Gate 14 cried. Not a scream. Not a collapse. He
              cries the way a body cries when every neurochemical buffer has been
              depleted and the parasympathetic system finally takes over. Quiet.
              Involuntary. The kind of crying that happens when you are finally safe
              enough to stop being strong.
            </p>
            <p>
              He eats through it. Because he is hungry. Because his caretaker taught
              him that the body's needs do not pause for the mind's crises. Because
              the food is warm and the room is quiet and for the first time since this
              morning he is not being watched by anyone.
            </p>
          </section>

          <Pulse />

          {/* The Window */}
          <section className="calc-section reveal">
            <h2>The Window</h2>
            <p>
              He stands at the glass with his plate empty and his eyes dry and the city
              doing its breathing below. His phone is on the table. He has not looked at
              it since the car. He does not want to see the Slack channel. He does not
              want to see Voss. He does not want to see the number in the banking app.
            </p>
            <p>
              He wants to call her.
            </p>
            <p>
              His caretaker. The woman who raised him. Who taught him the half-life of
              cortisol and the frequency of trust and the 400-millisecond window and
              then wrote two words and disappeared. He does not have her number. He has
              never had her number. She was always just there. In the kitchen. In the
              library. At the table with a green pen correcting his work.
            </p>
            <p>
              He does not know where she is. He does not know if she is watching. He
              does not know if she planned this or if she simply let it happen. He does
              not know if Seraphine Vael and his caretaker knew each other or if the
              universe just rhymes in ways that biochemistry cannot explain.
            </p>
            <p>
              He presses his forehead against the glass. It is cool. The temperature
              differential between his skin and the window sends a signal through his
              trigeminal nerve that his body processes as grounding. He learned that
              from her too. Cold glass on the forehead activates the dive reflex.
              Heart rate drops. Vagal tone increases. The body thinks it is entering
              water and conserves.
            </p>
            <p>
              He stays there for a long time. Forehead on glass. City below. The
              dive reflex holding his heart rate steady while his mind processes a
              day that began with a letter and ended with an empire.
            </p>
          </section>

          <Pulse />

          {/* The List */}
          <section className="calc-section reveal">
            <h2>The List</h2>
            <p>
              He picks up the phone at midnight. Not to check Slack. To open Notes.
            </p>
            <p>
              He adds to the list he started on the plane.
            </p>
            <p>
              <em>What I know:</em>
            </p>
            <p>
              <em>1. I own this company. No one else knows.</em>
            </p>
            <p>
              <em>2. Caelan Voss has been leading through fear for months. The
              organization's baseline cortisol is chronic.</em>
            </p>
            <p>
              <em>3. Loriann is the only uncontaminated node I have found. She
              booked the jet. She booked the hotel. She put food in the room that
              looks like home. She either researched me or she understands something
              about care that cannot be researched.</em>
            </p>
            <p>
              <em>4. The employees are afraid. Not of me. Of Voss. But tomorrow
              they will be afraid of both because Voss made the threat and I am the
              unknown variable.</em>
            </p>
            <p>
              <em>5. Cortisol from Voss's message will have decayed by morning.
              What remains will be resentment. Resentment is slower. Serotonin
              depleting. It will be in the room at 8 AM even though the fear will
              be gone.</em>
            </p>
            <p>
              <em>6. I will be there at 7. One hour before Voss's deadline. The
              first person they see will not be the man who threatened them. It
              will be me.</em>
            </p>
            <p>
              <em>What I do not know:</em>
            </p>
            <p>
              <em>1. How to lead.</em>
            </p>
            <p>
              <em>2. How to run a meeting.</em>
            </p>
            <p>
              <em>3. How to look four hundred people in the eye when one hundred
              of them made memes of my face.</em>
            </p>
            <p>
              <em>4. How to stand in a room and not absorb every nervous system
              in it.</em>
            </p>
            <p>
              <em>5. Whether I am the right person for this.</em>
            </p>
            <p>
              He stares at the second list. The list of things he does not know is
              longer than the list of things he does. His caretaker would say that
              is the correct ratio. She always said the person who thinks they know
              everything has stopped producing dopamine. The person who knows they
              know nothing is the one whose chemistry is still moving.
            </p>
            <p>
              He adds one more line.
            </p>
            <p>
              <em>6. What she would do if she were here.</em>
            </p>
            <p>
              He closes the phone. He turns off the lights. The city glows through
              the windows. He lies on a bed that is larger than his room at the cabin
              and stares at the ceiling and does not sleep for a very long time.
            </p>
            <p>
              At 4 AM, his body gives up fighting and drops him into unconsciousness.
              Not sleep. Chemical shutdown. The system running on empty finally hitting
              the wall.
            </p>
            <p>
              At 6 AM, the alarm goes off. He does not remember setting it. He picks
              up the phone. A text from a number he does not recognize.
            </p>
            <p>
              <em>"Your suit is hanging on the bathroom door. Charcoal. It was
              tailored from your application photo. The shoes are underneath. I
              took the liberty."</em>
            </p>
            <p>
              No name. No signature. But the handwriting of the text, the cadence,
              the precision, the assumption that he would need this and would not have
              thought to arrange it himself. He knows who it is. Not Loriann. Loriann
              texts with warmth.
            </p>
            <p>
              This text has the frequency of the butler. And the attorney. And the
              boarding agent. The flat professional register of people who serve
              without signaling.
            </p>
            <p>
              He walks to the bathroom. The suit is there. Charcoal gray. Simple.
              No insignia, no logo, no statement. The kind of suit that says nothing
              about itself and everything about the person wearing it. The shoes are
              black, polished, the right size.
            </p>
            <p>
              Tailored from his application photo. Someone looked at a picture of a
              Half-Orc and calculated the measurements of a body they had never
              touched and produced a suit that would fit him on his first day in a
              world that has never seen him.
            </p>
            <p>
              He puts it on. It fits. Of course it fits.
            </p>
            <p>
              He looks in the mirror. He has never worn a suit. He has never seen
              himself in anything other than the clothes his caretaker bought him. The
              person in the mirror is a stranger. Same tusks. Same orange eyes. Same
              size that fills a doorway and makes people step back. But in the suit,
              something shifts. Not for others. For him. The suit is armor that does
              not look like armor. It is a frequency modifier. It tells the observer's
              brain: this person belongs in rooms you belong in. Before the tusks
              register, the suit registers. The assessment begins with the clothing
              and works inward, and by the time the amygdala reaches the face, the
              prefrontal cortex has already filed the suit under "professional" and
              the conflict between the two signals buys him time.
            </p>
            <p>
              400 milliseconds for the face. But the suit lands in 200. That is the
              margin his caretaker could never give him from a cabin in the woods.
            </p>
            <p>
              He straightens the collar. He breathes. Four. Seven. Eight.
            </p>
            <p>
              He walks out of The Meridian at 6:47 AM. The office is two blocks away.
              He will be there before anyone. Before Voss. Before the fear. Before
              the resentment.
            </p>
            <p>
              The first person they see will not be the man who threatened them.
            </p>
            <p>
              It will be a Half-Orc in a charcoal suit who learned the frequency of
              trust in a cabin in the woods and is about to find out if it works
              anywhere else.
            </p>
          </section>

          <Pulse />

          {/* The Science */}
          <section className="calc-section reveal">
            <h2>The Science Behind Chapter 4</h2>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Parasympathetic Collapse</span>
              <code>if sustained_sympathetic_activation &gt; 14hrs &rarr; parasympathetic override &rarr; involuntary shutdown</code>
              <span className="calc-eq-desc">The body cannot sustain sympathetic (fight/flight) activation indefinitely. After prolonged stress, the parasympathetic system forces a reset. This is not sleep. It is neurochemical shutdown. The crying at the table, the forehead on the glass, the collapse at 4 AM. The body was processing what the mind could not yet hold. This is not weakness. It is the system protecting itself from its own chemistry.</span>
            </div>

            <div className="calc-equation">
              <span className="calc-eq-label">The Dive Reflex</span>
              <code>cold stimulus on trigeminal nerve &rarr; vagal activation &rarr; HR decrease + peripheral vasoconstriction</code>
              <span className="calc-eq-desc">Pressing a cold surface against the forehead activates the mammalian dive reflex through the trigeminal nerve. Heart rate drops. Blood redirects to vital organs. The body enters a conservation state that mimics the neurochemical profile of calm without requiring the mind to produce it. Gnul learned this from a textbook. He used it staring at a city he had never seen, grounding himself through physiology when psychology could not keep up.</span>
            </div>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Clothing as Frequency Modifier</span>
              <code>visual_assessment: clothing (200ms) &rarr; face (400ms) &rarr; if clothing = &quot;professional&quot; AND face = &quot;novel&quot; &rarr; conflict &rarr; extended processing</code>
              <span className="calc-eq-desc">The brain processes clothing before it processes facial features. A suit registers in approximately 200 milliseconds and is categorized as "professional" before the amygdala completes its 400-millisecond threat assessment of the face. When the two signals conflict (professional attire, unfamiliar face), the prefrontal cortex is recruited to resolve the contradiction. That recruitment buys time. Time is all Gnul needs for his voice to land.</span>
            </div>

            <div className="calc-equation">
              <span className="calc-eq-label">Environmental Serotonin Design</span>
              <code>low lighting + warm tones + cedar scent + consistent temperature = elevated serotonin baseline in occupant</code>
              <span className="calc-eq-desc">Luxury hospitality is serotonin engineering. Every element of The Meridian, the lighting, the cedar, the temperature, the marble, is designed to produce neurochemical stability in the guest. This is not aesthetics. It is architecture that understands that a calm nervous system spends more money, stays longer, and returns. The best hotels are the ones that have reverse-engineered serotonin without knowing the word for it.</span>
            </div>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Anticipatory Care vs. Reactive Care</span>
              <code>if need is met before request &rarr; OT spike in recipient = trust formation<br />if need is met after request &rarr; relief only, no OT bonus</code>
              <span className="calc-eq-desc">Loriann booked the hotel before he asked. The food was there before he was hungry. The suit arrived before he knew he needed one. Anticipatory care produces a stronger oxytocin response than reactive care because it signals that someone modeled your needs without being told. The recipient's nervous system reads this as: I am known. I am seen. Someone thought about me when I was not in the room. That is the neurochemical foundation of loyalty.</span>
            </div>
          </section>

          <Pulse />

          {/* Navigation */}
          <section className="calc-section reveal" style={{ textAlign: 'center', padding: '2rem 0' }}>
            <a href="/gnul/chapter-3" style={{ color: '#c9a84c', textDecoration: 'none', fontSize: '1.1rem' }}>&larr; Chapter 3: The Landing</a>
            <span style={{ color: '#333', margin: '0 2rem' }}>|</span>
            <a href="/gnul/chapter-5" style={{ color: '#c9a84c', textDecoration: 'none', fontSize: '1.1rem' }}>Chapter 5: The Lobby &rarr;</a>
          </section>

          {/* Signature */}
          <div className="calc-signature reveal">
            <p><strong>Nexus Concordat Inc.</strong></p>
            <p>Narrative Physics&trade; Leadership Development</p>
            <div className="patent-tags">
              <span className="patent-tag">Patent Pending: 63/939,190</span>
              <span className="patent-tag">Patent Pending: 63/962,385</span>
              <span className="patent-tag">Patent Pending: 63/988,485</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default GnulChapter4;
